export type KeyedListenerHandlerFinalizer = () => void;
export type KeyedListenerHandlerInitiator<K, L> = (key: K, listeners: L[]) => KeyedListenerHandlerFinalizer;

type Entry<L> = {
    finalizer: KeyedListenerHandlerFinalizer;
    listeners: L[];
};

export class KeyedListenerManager<Key, Listener> {
    private readonly _initiator: KeyedListenerHandlerInitiator<Key, Listener>;

    private readonly _entries = new Map<Key, Entry<Listener>>();

    constructor(initiator?: KeyedListenerHandlerInitiator<Key, Listener>) {
        this._initiator = initiator ?? (() => () => { /* */ });
    }

    addListener(key: Key, listener: Listener): void {
        let entry = this._entries.get(key);
        if (!entry) {
            const listeners = new Array<Listener>();
            const finalizer = this._initiator(key, listeners);
            entry = { finalizer, listeners };
            this._entries.set(key, entry);
        }
        entry.listeners.push(listener);
    }

    removeListener(key: Key, listener: Listener): void {
        const entry = this._entries.get(key);
        if (!entry) {
            console.warn(`an entry which being removed for key ${JSON.stringify(key)} is not found`);
            return;
        }

        const index = entry.listeners.indexOf(listener);
        if (index == -1) {
            console.warn(`the listener which being removed for key ${JSON.stringify(key)} is not found`);
            return;
        }

        entry.listeners.splice(index, 1);
        const x = entry.listeners.indexOf(listener);
        if (x != -1) {
            console.warn('cannot remove the listener; something odd');
        }

        if (entry.listeners.length == 0) {
            entry.finalizer();
            this._entries.delete(key);
        }
    }

    keys(): Key[] {
        return [...this._entries.keys()];
    }

    getListeners(key: Key): Listener[] | undefined {
        const entry = this._entries.get(key);
        if (!entry) return undefined;
        return entry.listeners;
    }

    forEach(key: Key, cb: (value: Listener, index: number, array: Listener[]) => void) {
        [...(this.getListeners(key) ?? [])].forEach(cb);
    }

    clear(): void {
        this._entries.forEach(entry => {
            entry.finalizer();
        });
        this._entries.clear();
    }
}

export function createKeyedListenerManager<Key, Listener>(initiator?: KeyedListenerHandlerInitiator<Key, Listener>) {
    return new KeyedListenerManager(initiator);
}
